import type { AppointmentType } from '@mend/types/appointment';
import type { BaseAppointmentType } from '#components/Autocomplete/BaseAppointmentTypes/BaseAppointmentTypesAutocomplete.types.ts';

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { AppointmentTypesAutocomplete } from '#components/Autocomplete/AppointmentTypesAutocomplete.tsx';
import { BaseAppointmentTypesAutocomplete } from '#components/Autocomplete/BaseAppointmentTypes/BaseAppointmentTypesAutocomplete.tsx';
import { appointmentTypes as getAppointmentTypesQueryOptions } from '#lib/react-query/queries.ts';
import { isBaseAppointmentType } from './AppointmentTypes.utils.ts';

const baseAppointmentTypeProps = {
  inputLabel: 'Base Appointment Type',
  inputHelperText:
    'If you select a base appointment type, appointment type selection will be filtered respectively.',
} as const;

const appointmentTypeProps = {
  inputLabel: 'Appointment Type',
  inputHelperText:
    "Appointment type selection takes priority: any base appointment types that don't match selected appointment types will be filtered out of the data below.",
} as const;

const sharedProps = {
  inputSize: 'medium',
  multiple: true,
  disableCloseOnSelect: true,
  limitTags: 1,
} as const;

export function AppointmentTypes(props: {
  defaultValue: {
    baseAppointmentTypes: BaseAppointmentType[];
    appointmentTypeIds: AppointmentType['id'][];
  };
}) {
  const [, setSearchParams] = useSearchParams();
  const appointmentTypesQuery = useQuery(getAppointmentTypesQueryOptions());

  // Cache initial values to prevent re-running effects
  const [initialBaseAppointmentTypes] = React.useState(
    () => props.defaultValue.baseAppointmentTypes
  );
  const [initialAppointmentTypeIds] = React.useState(
    () => props.defaultValue.appointmentTypeIds
  );

  // Track initialization state for async data
  const [hasDataInitialized, setHasDataInitialized] = React.useState(
    () => (appointmentTypesQuery.data ?? []).length > 0
  );

  const [selectedBaseAppointmentTypes, setSelectedBaseAppointmentTypes] =
    React.useState<BaseAppointmentType[]>(() => initialBaseAppointmentTypes);

  const [selectedAppointmentTypes, setSelectedAppointmentTypes] =
    React.useState(() =>
      initialAppointmentTypeIds.length > 0 && appointmentTypesQuery.data
        ? appointmentTypesQuery.data.filter((type) =>
            initialAppointmentTypeIds.includes(type.id)
          )
        : []
    );

  // Synchronize data with initial values once available
  React.useEffect(() => {
    if (
      !hasDataInitialized &&
      appointmentTypesQuery.data &&
      initialAppointmentTypeIds.length > 0
    ) {
      setSelectedAppointmentTypes(
        appointmentTypesQuery.data.filter((type) =>
          initialAppointmentTypeIds.includes(type.id)
        )
      );
      setHasDataInitialized(true);
    }
  }, [
    hasDataInitialized,
    appointmentTypesQuery.data,
    initialAppointmentTypeIds,
  ]);

  function updateBothSearchParams(
    baseAppointmentTypeValues: BaseAppointmentType[],
    appointmentTypeValues: AppointmentType[]
  ) {
    setSearchParams((searchParams) => {
      searchParams.delete('bat');
      searchParams.delete('atId');
      for (const value of baseAppointmentTypeValues) {
        searchParams.append('bat', value);
      }
      for (const value of appointmentTypeValues) {
        searchParams.append('atId', String(value.id));
      }
      return searchParams;
    });
  }

  if (appointmentTypesQuery.isLoading) {
    return (
      <>
        <BaseAppointmentTypesAutocomplete
          {...baseAppointmentTypeProps}
          inputSize="medium"
          value={null}
          disabled
        />

        <AppointmentTypesAutocomplete
          {...appointmentTypeProps}
          inputSize="medium"
          value={null}
          disabled
        />
      </>
    );
  }

  return (
    <>
      <BaseAppointmentTypesAutocomplete
        {...sharedProps}
        {...baseAppointmentTypeProps}
        value={selectedBaseAppointmentTypes}
        onChange={(_event, value, reason) => {
          setSelectedBaseAppointmentTypes(value);

          // Filter appointment types to match selected base appointment types
          // When base appointment types are cleared, clear appointment types too
          const newSelectedAppointmentTypes =
            value.length === 0
              ? []
              : selectedAppointmentTypes.filter(
                  (type) =>
                    isBaseAppointmentType(type.type) &&
                    value.includes(type.type)
                );

          setSelectedAppointmentTypes(newSelectedAppointmentTypes);

          // Update search params only when not selecting an option
          if (reason !== 'selectOption') {
            updateBothSearchParams(value, newSelectedAppointmentTypes);
          }
        }}
        onClose={() => {
          updateBothSearchParams(
            selectedBaseAppointmentTypes,
            selectedAppointmentTypes
          );
        }}
        data-testid="base-appointment-type-autocomplete-filter"
      />

      <AppointmentTypesAutocomplete
        {...sharedProps}
        {...appointmentTypeProps}
        inputHelperText="Appointment type selection takes priority: any base appointment types that don't match selected appointment types will be filtered out of the data below."
        value={selectedAppointmentTypes}
        onChange={(_event, value, reason) => {
          setSelectedAppointmentTypes(value);

          // Update search params only when not selecting an option
          if (reason !== 'selectOption') {
            updateBothSearchParams(selectedBaseAppointmentTypes, value);
          }
        }}
        onClose={() => {
          updateBothSearchParams(
            selectedBaseAppointmentTypes,
            selectedAppointmentTypes
          );
        }}
        preFilterOptions={(options) => {
          // Filter to only show valid base appointment types first
          const baseTypeOptions = options.filter((opt) =>
            isBaseAppointmentType(opt.type)
          );

          // If no base appointment types selected, show all base appointment types
          if (selectedBaseAppointmentTypes.length === 0) {
            return baseTypeOptions;
          }

          // Filter appointment types by selected base appointment types
          return options.filter(
            (appointmentType) =>
              isBaseAppointmentType(appointmentType.type) &&
              selectedBaseAppointmentTypes.includes(appointmentType.type)
          );
        }}
        data-testid="appointment-type-autocomplete-filter"
      />
    </>
  );
}
